import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UserGroupIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { clientsApi } from '../../api/clients';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import type { ClientWithPortal } from '@affogato/shared';

interface AssociateClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  formTitle: string;
  onAssociate: (clientId: string) => Promise<void>;
}

export function AssociateClientModal({
  isOpen,
  onClose,
  formTitle,
  onAssociate,
}: AssociateClientModalProps) {
  const { t } = useTranslation('diagnostics');
  const [clients, setClients] = useState<ClientWithPortal[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAssociating, setIsAssociating] = useState(false);
  const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadClients();
    }
  }, [isOpen]);

  const loadClients = async () => {
    setIsLoading(true);
    try {
      const response = await clientsApi.list();
      setClients(response.clients);
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssociate = async () => {
    if (!selectedClient) return;

    setIsAssociating(true);
    try {
      await onAssociate(selectedClient);
      onClose();
    } catch (error) {
      console.error('Error associating to client:', error);
    } finally {
      setIsAssociating(false);
    }
  };

  const filteredClients = clients.filter((client) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      client.name.toLowerCase().includes(query) ||
      client.company?.toLowerCase().includes(query) ||
      client.email?.toLowerCase().includes(query)
    );
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('associate.title') || 'Asociar a cliente'}
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-500 dark:text-slate-400">
          {t('associate.description') || `Selecciona el cliente al que deseas asociar el formulario "${formTitle}".`}
        </p>

        {/* Search */}
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={t('associate.searchClients') || 'Buscar clientes...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Clients List */}
        <div className="max-h-64 overflow-y-auto space-y-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="md" />
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="text-center py-8">
              <UserGroupIcon className="w-10 h-10 text-gray-300 dark:text-slate-600 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-slate-400">
                {searchQuery
                  ? t('associate.noResults') || 'No se encontraron clientes'
                  : t('associate.noClients') || 'No hay clientes disponibles'}
              </p>
            </div>
          ) : (
            filteredClients.map((client) => (
              <button
                key={client.id}
                onClick={() => setSelectedClient(client.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                  selectedClient === client.id
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
                }`}
              >
                <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-medium text-gray-600 dark:text-slate-300">
                    {client.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white truncate">
                    {client.name}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-slate-400 truncate">
                    {client.company || client.email}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-slate-700">
          <Button variant="ghost" onClick={onClose}>
            {t('create.cancel') || 'Cancelar'}
          </Button>
          <Button
            onClick={handleAssociate}
            disabled={!selectedClient || isAssociating}
          >
            {isAssociating ? <Spinner size="sm" /> : t('associate.associate') || 'Asociar'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
